import React, { useEffect, useRef } from 'react'
import i18n from 'i18next'

import { capitalizeFirstLetter } from '../../utils/helpers'
import HoverInfo from '../../UniformComponents/HoverInfo'

const HeatMap = ({
    chartData, year, dashbaordStore, title, segmentSummaryHeight, segmentNameOverride,
}) => {
    let elRef = useRef(null)
    let yAxisRef = useRef(null)

    const yearData = chartData.find((d) => d.year === year)

    const noData = () => (
        <div style={{
            width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
        }}
        >
            <img style={{ width: '150px' }} src={`${process.env.CDN_URL}/images/search-icon-v2.svg`} />
            <div style={{
                fontSize: '20px', marginTop: '10px', fontWeight: '700', color: '#8B99A7',
            }}
            >
                No data has been found
            </div>
        </div>
    )

    if (!yearData) return noData

    const addFilter = (segment) => {
        const { addDashboardFilters } = dashbaordStore
        const data = yearData.surveyDataIds[segment.toLowerCase()]

        dashbaordStore.filterKpis.push({
            id: 'heat',
            options: [
                {
                    value: data.join('_'),
                    label: segment,
                    surveyDataIds: data,
                },
            ],
        })

        addDashboardFilters({
            respondentType: 'EX',
            question: 'heat',
            filterLabel: title,
            filterType: 'single',
            options: [
                {
                    value: data.join('_'),
                    label: segment,
                    surveyDataIds: data,
                },
            ],
        })
        dashbaordStore.init(true)
    }

    const heatMap = [
        [
            [
                [3, 0], [3, 1],
                [2, 0], [2, 1],
            ],
            [
                [3, 2], [3, 3],
                [2, 2], [2, 3],
            ],
        ],
        [
            [
                [1, 0], [1, 1],
                [0, 0], [0, 1],
            ],
            [
                [1, 2], [1, 3],
                [0, 2], [0, 3],
            ],
        ],
    ]
    const { data, options: dataOptions } = yearData
    const options = [
        [dataOptions?.[0]?.[0 ?? ''], dataOptions?.[1]?.[0] ?? ''],
        [dataOptions?.[2]?.[0 ?? ''], dataOptions?.[3]?.[0] ?? ''],
    ]
    const colours = [
        [[250, 234, 76], [89, 179, 99]],
        [[232, 85, 95], [245, 161, 97]],
    ]
    const total = heatMap.reduce((total, rows) => (
        rows.reduce((rowTotal, cols) => (
            cols.reduce((colTotal, [y, x]) => (
                (data?.[y]?.[x] ?? 0) + colTotal
            ), rowTotal)
        ), total)
    ), 0)

    const buildSegment = (rowIndex, colIndex) => {
        const [r, g, b] = colours[rowIndex][colIndex]
        const cells = heatMap[rowIndex][colIndex]
        const segmentOpt = options?.[rowIndex]?.[colIndex]
        const segment = segmentNameOverride
            ? segmentNameOverride[segmentOpt]
            : capitalizeFirstLetter(segmentOpt ?? '')
        const segmentTotal = cells.reduce((total, [y, x]) => (data[y][x] + total), 0)
        const segmentPct = segmentTotal / (total || 1) * 100

        return (
            <div
                className="segment"
            >
                <div
                    style={{
                        backgroundColor: `rgba(${r}, ${g}, ${b}, 1)`,
                        textAlign: 'center',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '5px',
                    }}
                >
                    {segment}
                    {' '}
                    (
                    {parseInt(Math.round(segmentPct), 10)}
                    %)
                    <HoverInfo
                        cssClass="hover-info"
                        title={i18n.t(`dashboard.EEM.${segmentOpt.toLowerCase()}.title`)}
                        desc={i18n.t(`dashboard.EEM.${segmentOpt.toLowerCase()}`)}
                        dir="bottom"
                        usePortal
                    >
                        <i className="info-icon fa-solid fa-circle-question" />
                    </HoverInfo>
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        flex: 1,
                        marginTop: '8px',
                    }}
                >
                    {cells.map((cel) => {
                        const [y, x] = cel
                        const count = data[y][x]
                        const pct = count / (total || 1) * 100
                        const alpha = Math.max(0.1, count / (segmentTotal || 1))
                        return (
                            <div
                                className="segmentCell"
                                style={{
                                    backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})`,
                                }}
                            >
                                {count}
                                <div
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: '400',
                                    }}
                                >
                                    (
                                    {parseInt(Math.round(pct), 10)}
                                    %)
                                </div>
                            </div>
                        )
                    })}

                </div>
            </div>
        )
    }

    useEffect(() => {
        if (total === 0) return
        yAxisRef.style.width = `${elRef.getBoundingClientRect().height}px`
    })

    if (total === 0) {
        return noData
    }

    return (
        <div
            className="heatMap"
            style={segmentSummaryHeight ? { height: `calc(100% - ${40 + segmentSummaryHeight}px)` } : undefined}
        >
            <div
                className="heatMapContainer"
                ref={(el) => { elRef = el }}
            >
                {heatMap.map((row, rowIndex) => (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                gap: '24px',
                                flex: 1,
                            }}
                        >
                            {row.map((_, colIndex) => (
                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                    }}
                                >
                                    {buildSegment(rowIndex, colIndex)}
                                </div>
                            ))}
                        </div>
                    </>
                ))}
                <div
                    className="yAxis"
                    ref={(el) => yAxisRef = el}
                >
                    {i18n.t('dashboard.HeatMap.yAxisTitle')}
                </div>
                <div
                    className="xAxis"
                >
                    {i18n.t('dashboard.HeatMap.xAxisTitle')}
                </div>
            </div>
        </div>
    )
}

export default HeatMap
